import {sendAnalyticsEvent} from '#/analytics/analytics';
import {NoticeMessage} from '#/atoms/notice-message';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {IconButton} from '#/components/buttons/icon-button';
import {RightArrowLoadingButton} from '#/components/buttons/right-arrow-loading-button';
import {LinkOpenGraph, LinkOpenGraphLoading} from '#/components/land-comment/link-open-graph';
import {LandCommentActions} from '#/components/land-comment/use-land-comment-actions';
import {useModalContext} from '#/contexts/modal-provider';
import {useBlockingLoginAction} from '#/hooks/use-blocking-login-action';
import {useCharacterRange} from '#/hooks/use-character-range';
import {useInputText, useInputUrl} from '#/hooks/use-input';
import {useOpenGraphLinkPreview} from '#/hooks/use-open-graph-link-preview';
import {XMarkIcon} from '@heroicons/react/24/solid';
import {FormEvent, FunctionComponent, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

type LandCommentEditorProps = {
  maxCharacterCount?: number;
  onSuccess: () => void;
  onCreate: LandCommentActions['onCreate'];
};

export const LandCommentEditor: FunctionComponent<LandCommentEditorProps> = ({onCreate, onSuccess}) => {
  const {t} = useTranslation('translation');

  const {land, landMarketInfo, landAccountInfo} = useContext(ActionMenuContext);
  const {open} = useModalContext();
  // Comment Input
  const {data: inputText, onChange: onTextInputChange, reset: resetTextInput} = useInputText();
  const characterRange = useCharacterRange({
    text: inputText,
    max: 5012,
    noticeBuffer: 25,
  });

  // Link Input
  const {data: inputUrl, isValid: isInputUrlValid, onChange: onLinkInputChange, reset: resetLinkInput} = useInputUrl();

  // UI
  const [isOpen, setIsOpen] = useState(false);

  // Open Graph Link Preview
  const {
    data: openGraphLinkPreview,
    isLoading: isOpenGraphLinkPreviewLoading,
    reset: resetOpenGraphLinkPreview,
  } = useOpenGraphLinkPreview(inputUrl, isInputUrlValid);

  // Submit Statuses
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitError = useLandCommentEditorSubmitError(inputText, inputUrl);

  const canSubmit = !!inputText.length && !characterRange.isOver() && (!!inputUrl ? isInputUrlValid : true);

  const createComment = useBlockingLoginAction(async () => {
    if (!landAccountInfo) {
      open({name: 'LoginModal', props: {}});
      return;
    }
    if (!canSubmit) {
      return;
    }

    submitError.reset();
    setIsSubmitting(true);

    try {
      const createResponse = await onCreate({
        text: inputText,
        link: !!inputUrl ? inputUrl : undefined,
      });

      if (!createResponse) {
        throw new Error();
      }

      if (createResponse.status === 'success') {
        sendAnalyticsEvent.propertyComment({
          land,
          landMarketInfo,
          linkShared: !!inputUrl,
        });

        resetTextInput();
        resetLinkInput();
        resetOpenGraphLinkPreview();
        onSuccess();
      } else {
        const {payload} = createResponse;
        const inappropriateComment = payload.personal || payload.profanity || payload.textLinks;
        if (payload.link && inappropriateComment) {
          submitError.set(
            t(
              'pages.actionMenu.information.sections.community.landCommentEditor.errors.commentAndLinkUnallowedContent',
            ),
          );
        } else if (payload.link) {
          submitError.set(
            t('pages.actionMenu.information.sections.community.landCommentEditor.errors.linkUnallowedContent'),
          );
        } else {
          submitError.set(
            t('pages.actionMenu.information.sections.community.landCommentEditor.errors.commentUnallowedContent'),
          );
        }
      }
    } catch {
      submitError.set(
        t('pages.actionMenu.information.sections.community.landCommentEditor.errors.errorOccuredWhileSubmitting'),
      );
    }

    setIsSubmitting(false);
  });

  // Functions
  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    createComment();
  };

  const onClearLinkClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    resetLinkInput();
    resetOpenGraphLinkPreview();
  };

  useEffect(() => {
    if (!isOpen && inputText.length > 0) {
      setIsOpen(true);
      return;
    }

    if (isOpen && !inputText && !inputUrl) {
      setIsOpen(false);
    }
  }, [isOpen, inputText, inputUrl]);

  return (
    <form
      onSubmit={onSubmit}
      className={`grid auto-rows-min grid-cols-[auto,24px] items-center gap-2 p-3 text-sm text-gray-400 overflow-hidden bg-graybluedark ${
        isOpen ? 'rounded-xl' : 'rounded-full'
      }`}
    >
      <div
        className={`w-full ${
          isOpen ? 'h-16 col-start-1 col-span-2 border-b border-gray-700' : 'h-full col-start-1 col-span-1'
        } flex flex-col bg-transparent relative`}
      >
        <textarea
          rows={1}
          placeholder={t('pages.actionMenu.information.sections.community.landCommentEditor.textPlaceholder')}
          className='mt-0.5 w-full h-full bg-transparent resize-none outline-none caret-deepblue text-white ml-1'
          value={inputText}
          onChange={onTextInputChange}
          disabled={false}
        />

        {isOpen && characterRange.showBuffer() && (
          <div className='p-1 text-xs text-gray-400 self-end'>
            {t('pages.actionMenu.information.sections.community.landCommentEditor.textCharacterCount', {
              count: characterRange.count(),
              maxCount: characterRange.total(),
            })}
          </div>
        )}
      </div>

      {isOpen && (
        <div className='flex justify-end items-center gap-4 row-start-2 row-span-1 col-start-1 col-span-1 ml-1 overflow-hidden'>
          <input
            className='grow bg-transparent outline-none caret-deepblue truncate text-white invalid:underline invalid:decoration-red-500'
            type='url'
            name='url'
            id='url'
            value={inputUrl}
            onChange={onLinkInputChange}
            placeholder={t('pages.actionMenu.information.sections.community.landCommentEditor.linkPlaceholder')}
            pattern='https://.*'
          />

          {!!inputUrl && (
            <IconButton
              type='button'
              IconComponent={XMarkIcon}
              iconClassName={'w-4 h-4 fill-gray-400 stroke-gray-400'}
              onClick={onClearLinkClick}
            />
          )}
        </div>
      )}

      <div className='col-start-2 col-span-1'>
        <RightArrowLoadingButton type='submit' enabled={canSubmit} loading={isSubmitting} />
      </div>

      {isOpen && (isOpenGraphLinkPreviewLoading || openGraphLinkPreview) && (
        <div className='w-full row-start-3 row-span-1 col-start-1 col-span-2 pl-1'>
          {isOpenGraphLinkPreviewLoading ? (
            <LinkOpenGraphLoading />
          ) : (
            openGraphLinkPreview && <LinkOpenGraph openGraphLink={openGraphLinkPreview} />
          )}
        </div>
      )}

      {!!submitError.data && (
        <NoticeMessage className='mt-0 col-span-2' theme={'error'}>
          {submitError.data}
        </NoticeMessage>
      )}
    </form>
  );
};

export const useLandCommentEditorSubmitError = (
  inputText: string,
  inputUrl: string,
): {
  data: string | undefined;
  set: (error: string) => void;
  reset: () => void;
} => {
  const [submitError, setSubmitError] = useState<string | undefined>();

  useEffect(() => {
    if (submitError && !inputText && !inputUrl) {
      setSubmitError(undefined);
    }
  }, [inputText, inputUrl, submitError]);

  return {
    data: submitError,
    set: setSubmitError,
    reset: () => setSubmitError(undefined),
  };
};
